import { Field, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useCallback } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { EmailInput } from '@hivebrite/design-system/Inputs';

import shouldDisplayError from 'hb-react/shared/components/formik/utils/shouldDisplayError';

import { validateEmail } from './validate';

const Email = (
  /** @type {import ('./types').EmailProps} */
  {
    ariaDescribedBy,
    ariaLabel,
    disabled = false,
    icon,
    id,
    info,
    infoIcon,
    label,
    labelMultiline = true,
    name,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    tooltip,
    validate = {},
    wrapperProps = {},
  },
) => {
  const formik = useFormikContext();
  const htmlId = id || name;
  const { required, maxLength } = validate;

  const handleValidate = useCallback(
    (value) =>
      validateEmail({
        maxLength,
        required,
        value,
      }),
    [maxLength, required],
  );

  const handleChange = useCallback(
    (e) => {
      formik.handleChange(e);
      if (onChange) {
        onChange(e.target.value);
      }
    },
    [formik, onChange],
  );

  const handleBlur = useCallback(
    (e) => {
      formik.handleBlur(e);
      if (onBlur) {
        onBlur();
      }
    },
    [formik, onBlur],
  );

  return (
    <Box mb={6} {...wrapperProps}>
      <Field name={name} validate={handleValidate}>
        {({
          field, // { name, value, onChange, onBlur }
          meta, // { error, touched, value }
          form,
        }) => (
          <EmailInput
            {...field}
            ariaDescribedBy={ariaDescribedBy}
            ariaLabel={ariaLabel}
            autoComplete="email"
            clearable
            dataTestId="formik-email"
            disabled={disabled}
            error={shouldDisplayError({ meta, form })}
            icon={icon}
            id={htmlId}
            initialValue={meta.initialValue}
            info={info}
            infoIcon={infoIcon}
            label={label}
            labelMultiline={labelMultiline}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={onFocus}
            placeholder={placeholder}
            required={required}
            tooltip={tooltip}
            touched={meta.touched}
          />
        )}
      </Field>
    </Box>
  );
};

Email.propTypes = {
  name: PropTypes.string.isRequired,
  ariaDescribedBy: PropTypes.string,
  ariaLabel: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.string,
  labelMultiline: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  tooltip: PropTypes.string,
  validate: PropTypes.shape({
    required: PropTypes.bool,
    maxLength: PropTypes.number,
  }),
  wrapperProps: PropTypes.object,
};

export default Email;
